/**
 * Decision and copy for the "use a card you've already saved with us?" prompt shown
 * before a credit-pack checkout (#473).
 *
 * Deliberately free of imports so it can be unit-tested directly. The shapes below are
 * the minimum this module consumes; the wire types satisfy them structurally.
 */

/** The parts of a saved card needed to name it in the prompt. */
export interface PromptableCard {
    brand?: string;
    last4?: string;
}

/** What GET api/ai-credits/saved-cards tells us. */
export interface PromptableSavedCards {
    cards: PromptableCard[];
    consentNeeded: boolean;
}

/**
 * Stripe emits lower-case brand tokens. Anything unlisted is title-cased rather than
 * dropped, so a brand we haven't met still reads as a brand.
 */
const BRAND_LABELS: { [token: string]: string } = {
    amex: 'American Express',
    cartes_bancaires: 'Cartes Bancaires',
    diners: 'Diners Club',
    discover: 'Discover',
    eftpos_au: 'Eftpos Australia',
    jcb: 'JCB',
    mastercard: 'Mastercard',
    unionpay: 'UnionPay',
    visa: 'Visa',
};

function brandLabel(brand?: string): string {
    const token = (brand || '').trim().toLowerCase();
    if (!token) return 'Card';
    if (BRAND_LABELS[token]) return BRAND_LABELS[token];

    return token
        .split(/[\s_]+/)
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
}

/** e.g. "Visa •••• 4242", or just the brand when Stripe gave us no last4. */
export function describeSavedCard(card: PromptableCard): string {
    const label = brandLabel(card && card.brand);
    const last4 = card && card.last4 ? String(card.last4).trim() : '';

    return last4 ? label + ' •••• ' + last4 : label;
}

/**
 * Whether to interrupt the purchase with the consent prompt.
 *
 * False whenever there is nothing to ask about — notably a failed lookup, which must
 * never block a purchase — and the buyer goes straight to Checkout exactly as before.
 */
export function shouldPromptForSavedCards(
    saved: PromptableSavedCards | null | undefined,
): saved is PromptableSavedCards {
    if (!saved || !saved.consentNeeded) return false;

    return !!saved.cards && saved.cards.length > 0;
}

/**
 * Markdown body for the prompt. Names every card, because consent covers all of them —
 * a vague "use a saved card?" would be agreeing to something the buyer can't see.
 */
export function savedCardPromptMarkdown(cards: PromptableCard[]): string {
    const list = cards || [];

    if (list.length === 1) {
        return 'You already have **' + describeSavedCard(list[0]) + '** saved with us.\n\n'
            + 'Use it for this purchase instead of typing your card details again?';
    }

    const bullets = list.map(card => '- **' + describeSavedCard(card) + '**').join('\n');

    return 'You already have these cards saved with us:\n\n'
        + bullets
        + '\n\nMake them available at checkout so you don\'t have to type your card details again?';
}
